import {
  Users,
  Target,
  Eye,
  Heart,
  Shield,
  Sparkles,
  BookOpen,
  Award,
} from "lucide-react";
import safetyImage from "../../assets/8fe95c1ca0127b2e73df5cd086ff2f50d93364b0.png";
import governanceImage from "../../assets/3bb52aefa9af89fee05faf626ae4d95bbce2c6e9.png";
import financialSupportImage from "../../assets/5067da81442a15afffa4043118545076db48d65e.png";
import jainValuesImage from "../../assets/f682cf20caab70d5505a367ae317164965724c53.png";
import livingConditionsImage from "../../assets/f7260d07ff3524ee6a54f18681254116c79140b4.png";
import digitalEcosystemImage from "../../assets/5cf7786aad5f6e77c059678dcd6425a7edae4c32.png";
import heroImage from "../../assets/f1f8ea5ac8d415c490267141e14d7525b3eeb717.png";

export default function AboutPage() {
  const leadership = [
    { title: "Board of Governors", icon: <Award className="h-8 w-8" /> },
    {
      title: "Advisory Council of Jain Community Leaders",
      icon: <Users className="h-8 w-8" />,
    },
    {
      title: "National Academic & Welfare Committee",
      icon: <BookOpen className="h-8 w-8" />,
    },
    {
      title: "Regional Hostel Development Councils",
      icon: <Target className="h-8 w-8" />,
    },
    {
      title: "JBF Youth & Alumni Network",
      icon: <Sparkles className="h-8 w-8" />,
    },
  ];

  const jainValues = [
    {
      title: "Ahimsa",
      subtitle: "(Non-Violence)",
      description:
        "Practicing compassion and non-violence in all aspects of life",
      icon: <Heart className="h-12 w-12" />,
    },
    {
      title: "Aparigraha",
      subtitle: "(Non-Possessiveness)",
      description: "Embracing simplicity and non-attachment",
      icon: <Shield className="h-12 w-12" />,
    },
    {
      title: "Satya",
      subtitle: "(Truth)",
      description: "Upholding truthfulness and integrity in all endeavors",
      icon: <Eye className="h-12 w-12" />,
    },
    {
      title: "Shuddhata",
      subtitle: "(Purity)",
      description: "Maintaining purity of thought, word, and action",
      icon: <Sparkles className="h-12 w-12" />,
    },
    {
      title: "Seva",
      subtitle: "(Service)",
      description: "Dedicated service to students and community",
      icon: <Users className="h-12 w-12" />,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Image - Matching Figma */}
      <section className="relative flex min-h-[40vh] items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Team collaboration"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#7A1E1E]/80" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 text-center text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            {/* Our Story Badge */}
            <div className="mb-4 inline-flex items-center rounded-full bg-[#D4A017] px-3 py-1.5 text-xs font-medium text-white sm:mb-6 sm:px-4 sm:py-2 sm:text-sm">
              Our Story
            </div>
            {/* Main Title */}
            <h1 className="mb-4 text-3xl font-bold sm:mb-6 sm:text-4xl md:text-5xl lg:text-6xl">
              About JBF
            </h1>
            <p className="text-base leading-relaxed text-white sm:text-lg lg:text-xl">
              India&apos;s premier federation dedicated to transforming Jain
              student hostels through governance, technology, and community
              empowerment.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership Structure - Timeline Style */}
      <section className="bg-gradient-to-b from-white to-[#FFF5E6] py-12 sm:py-16 lg:py-20 xl:py-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center sm:mb-12 lg:mb-16">
            <h2 className="mb-4 text-2xl text-[#7A1E1E] sm:mb-6 sm:text-3xl md:text-4xl lg:text-5xl">
              Leadership & Structure
            </h2>
            <p className="mx-auto max-w-3xl text-base text-gray-600 sm:text-lg lg:text-xl">
              A robust organizational structure ensuring effective governance
              and community engagement.
            </p>
          </div>

          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute bottom-0 left-1/2 top-0 hidden w-0.5 bg-gradient-to-b from-[#D4A017] via-[#7A1E1E] to-[#D4A017] lg:block" />

            <div className="space-y-12">
              {leadership.map((item, index) => (
                <div
                  key={item.title}
                  className={`flex flex-col items-center gap-8 lg:flex-row ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                    }`}
                >
                  <div
                    className={`flex-1 ${index % 2 === 0 ? "lg:text-right" : "lg:text-left"}`}
                  >
                    <div className="inline-block rounded-2xl bg-white p-8 shadow-lg">
                      <h3 className="text-xl text-[#7A1E1E]">{item.title}</h3>
                    </div>
                  </div>

                  <div className="relative z-10">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white shadow-xl">
                      {item.icon}
                    </div>
                  </div>

                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Identity & Mandate - Grid Layout */}
      <section className="bg-white py-20 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <div className="mb-4 inline-block rounded-full bg-[#FFF9F0] px-4 py-2 text-sm text-[#7A1E1E]">
              Our Mission
            </div>
            <h2 className="mb-6 text-4xl text-[#7A1E1E] lg:text-5xl">
              Our Identity & Mandate
            </h2>
            <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-600">
              The Jain Boarding Federation (JBF) is a national umbrella
              organization bringing together Jain-run hostels & student welfare
              institutions across India.
            </p>
          </div>

          {/* 3-Column Layout */}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                title: "Standardizing Governance",
                description:
                  "Implementing world-class governance frameworks across all member institutions",
                icon: <Shield className="h-10 w-10" />,
                image: governanceImage,
              },
              {
                title: "Improving Living Conditions",
                description:
                  "Upgrading infrastructure and ensuring quality hostel facilities",
                icon: <Heart className="h-10 w-10" />,
                image: livingConditionsImage,
              },
              {
                title: "Jain Values & Ethics",
                description:
                  "Ensuring adherence to Jain principles and ethical conduct",
                icon: <Sparkles className="h-10 w-10" />,
                image: jainValuesImage,
              },
              {
                title: "Safety & Development",
                description:
                  "Promoting safety, nutrition, education, and holistic development",
                icon: <Target className="h-10 w-10" />,
                image: safetyImage,
              },
              {
                title: "Digital Ecosystem",
                description: "Building a digital ecosystem for transparency",
                icon: <Eye className="h-10 w-10" />,
                image: digitalEcosystemImage,
              },
              {
                title: "Financial Support",
                description:
                  "Providing help to financially disadvantaged Jain students",
                icon: <Users className="h-10 w-10" />,
                image: financialSupportImage,
              },
            ].map(item => (
              <div key={item.title} className="group">
                <div className="relative mb-6 h-64 overflow-hidden rounded-2xl">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full transform object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* <div className="absolute inset-0 bg-gradient-to-t from-[#7A1E1E]/80 to-transparent" /> */}
                  {/* <div className="absolute bottom-6 left-6">
                    <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-white text-[#7A1E1E]">
                      {item.icon}
                    </div>
                  </div> */}
                </div>
                <h3 className="mb-3 text-2xl text-[#7A1E1E]">{item.title}</h3>
                <p className="leading-relaxed text-gray-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jain Ethos - Circular Layout */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-32">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#D4A017]" />
          <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#7A1E1E]" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl text-[#7A1E1E] lg:text-5xl">
              The Jain Ethos at the Heart of JBF
            </h2>
            <p className="mx-auto max-w-3xl text-xl text-gray-600">
              Our values define our educational and welfare philosophy, guiding
              everything we do.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
            {jainValues.map(value => (
              <div key={value.title} className="group relative">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#7A1E1E] to-[#913535] opacity-0 blur-sm transition-opacity group-hover:opacity-45" />
                <div className="relative h-full rounded-2xl border-2 border-[#D4A017]/20 bg-gradient-to-br from-[#FFF9F0] to-white p-8 text-center transition-all hover:border-[#cca135]">
                  <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] text-white transition-transform group-hover:scale-110">
                    {value.icon}
                  </div>
                  <h3 className="mb-2 text-2xl text-[#7A1E1E]">
                    {value.title}
                  </h3>
                  <p className="mb-3 text-sm text-[#D4A017]">
                    {value.subtitle}
                  </p>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission - Split Design */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-[#FFF5E6] py-20 text-white lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-[#D4A017] blur-3xl" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-[#D4A017] blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            <div className="relative h-full">
              {/* <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] blur-sm" /> */}
              <div className="relative flex h-full flex-col rounded-3xl border border-white/20 bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] p-10 backdrop-blur-sm">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-[#D4A017] to-[#B8900F]">
                  <Eye className="h-10 w-10 text-white" />
                </div>
                <h2 className="mb-6 text-center text-4xl">Our Vision</h2>
                <p className="flex flex-1 items-center text-center text-lg leading-relaxed text-gray-100">
                  A future where every Jain student across India has access to
                  safe, modern, well-governed hostels that offer mentorship,
                  academic support, personal growth, and opportunities for
                  national and global careers.
                </p>
              </div>
            </div>

            <div className="relative h-full">
              {/* <div className="absolute -inset-4 rounded-3xl bg-[#D4A017]/20 blur-2xl" /> */}
              <div className="relative flex h-full flex-col rounded-3xl border border-white/20 bg-gradient-to-br from-[#7A1E1E] to-[#8B2E2E] p-10 backdrop-blur-sm">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-[#D4A017] to-[#B8900F]">
                  <Target className="h-10 w-10 text-white" />
                </div>
                <h2 className="mb-6 text-center text-4xl">Our Mission</h2>
                <p className="flex flex-1 items-center text-center text-lg leading-relaxed text-gray-100">
                  To create India&apos;s largest and most trusted
                  community-based hostel federation powered by governance, data,
                  technology, and Jain values.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
